import { Box, Divider, Heading, HStack, Text, VStack } from '@chakra-ui/react';
import { BerryLoader } from '@web-app/components/BerryLoader';
import { BenefitsHeaderV2 } from '@web-app/components/CallV2/BenefitsHeaderV2/BenefitsHeaderV2';
import { PolicyBenefitsV2 } from '@web-app/components/CallV2/PolicyBenefitsV2/PolicyBenefitsV2';
import {
  InsuranceDateText,
  VisitsBar,
} from '@web-app/components/ChartingNoteEditor/Components/Benefits/Components';
import { InsuranceDetails } from '@web-app/graphql/healthie/queries/get-client-insurance-details/types';
import DOMPurify from 'dompurify';

export interface InsuranceV2UIProps {
  clientId: string;
  insuranceData: InsuranceDetails | null;
  isPending: boolean;
  isError: boolean;
}

/**
 * UI component displaying insurance and benefits information.
 * This is a pure presentation component that receives all data as props.
 */
export const InsuranceV2UI: React.FC<InsuranceV2UIProps> = ({
  clientId,
  insuranceData,
  isPending,
  isError,
}) => {
  // Loading state
  if (isPending) {
    return (
      <Box textAlign="center" py={2}>
        <BerryLoader height="100px" />
      </Box>
    );
  }

  // Error state
  if (isError) {
    return (
      <Box background="" borderRadius={4} padding={2} width="100%">
        <Text align="center" color="status.error">
          Error loading insurance information. Please try again later.
        </Text>
      </Box>
    );
  }

  // No data state
  if (!insuranceData) {
    return <Text align="center">Insurance information unavailable</Text>;
  }

  const statsShown =
    insuranceData &&
    !insuranceData.hasSelfPay &&
    !!insuranceData.insurancePlan?.payerName;

  return (
    <Box padding={2} width="100%">
      <VStack
        gap={2}
        align="stretch"
        paddingBottom={1}
        divider={<Divider borderColor="border.default" />}
      >
        <BenefitsHeaderV2 clientId={clientId} data={insuranceData} />

        {statsShown && (
          <>
            <HStack justify="space-between" width="100%">
              <Heading
                size="sm"
                fontWeight={600}
                fontFamily="Plus Jakarta Sans, sans-serif"
              >
                Insurance Details
              </Heading>
            </HStack>
            <VStack align="flex-start" padding={2} gap={2}>
              <VisitsBar data={insuranceData.insuranceAuthorization} />

              <HStack justifyContent="space-between" width="100%">
                <InsuranceDateText
                  date={insuranceData.insuranceAuthorization?.startOn}
                  label="Start date"
                />
                <InsuranceDateText
                  date={insuranceData.insuranceAuthorization?.endOn}
                  label="Policy expiration date"
                  align="flex-end"
                />
              </HStack>
            </VStack>

            <PolicyBenefitsV2 benefits={insuranceData.benefits} />
          </>
        )}

        {/* Benefit Notes Section */}
        {insuranceData?.insuranceNotes && (
          <VStack gap={0} align="flex-start">
            <Text fontWeight={600} fontSize={14} lineHeight="20px">
              Benefit check notes
            </Text>

            <Text
              opacity={0.7}
              fontSize={14}
              lineHeight="20px"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  insuranceData.insuranceNotes || 'N/A',
                ),
              }}
            />
          </VStack>
        )}
      </VStack>
    </Box>
  );
};

InsuranceV2UI.displayName = 'InsuranceV2UI';
